import { createAsyncThunk } from "@reduxjs/toolkit";

import { DEV_MODE } from "../../../Constant/app/process";
import {
    getLeadHistory,
    retryLeadHistory,
} from "../../../helpers/leads/reports";
import {
    applyDevMockRetry,
    buildDevMockLeadHistory,
} from "./leadHistory.mock";
import type {
    GetLeadHistoryAsyncPayload,
    RetryLeadHistoryPayload,
} from "./leadHistory.types";

export const getLeadHistoryAsync = createAsyncThunk(
    "leadHistory/getLeadHistory",
    async (payload: GetLeadHistoryAsyncPayload, { rejectWithValue }) => {
        const { connectionId, ...params } = payload;
        const response = await getLeadHistory(
            connectionId,
            params,
            !DEV_MODE,
        );
        const leads = response.data?.leads;

        // Same rule as failed leads: a real payload wins, an empty or failed
        // one is replaced so the History screen can be exercised in DEV_MODE.
        if (DEV_MODE && !(response.status && leads?.length)) {
            return buildDevMockLeadHistory(params);
        }

        if (!response.status || !response.data) {
            return rejectWithValue(
                response.message ?? "Failed to load lead history",
            );
        }

        return response.data;
    },
);

export const retryLeadHistoryAsync = createAsyncThunk(
    "leadHistory/retry",
    async (payload: RetryLeadHistoryPayload, { dispatch, rejectWithValue }) => {
        const response = await retryLeadHistory(
            payload.connectionId,
            {
                stat_ids: payload.stat_ids,
                source: payload.source,
            },
            !DEV_MODE,
        );

        if (!response.status) {
            if (!DEV_MODE) {
                return rejectWithValue(response.message ?? "Retry failed");
            }

            const mockError = applyDevMockRetry(payload.stat_ids);
            if (mockError) return rejectWithValue(mockError);
        }

        if (payload.source === "history" && payload.listParams) {
            await dispatch(
                getLeadHistoryAsync({
                    connectionId: payload.connectionId,
                    ...payload.listParams,
                }),
            );
        }

        return payload.stat_ids;
    },
);
